/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/bluematePeriodSelector.scss';
import {AwesomeIcon, hasHover} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';

import classNames from 'classnames';

import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {PeriodLabel, translatePeriodLabel} from '../../../util/enums';

import {BluemateControlShape, BluemateControlSize, PeriodRangeFunc} from '../../../util/types';

import BluemateButton from '../button/bluemateButton';

import DvrdDatePicker from '../date/dvrdDatePicker';
import {PresetPeriod} from '../../../util/interfaces.ts';
import {PRESET_PERIODS} from '../../../util/dateUtil.ts';

const DEFAULT_PRESET = PeriodLabel.LAST_30_DAYS;

interface Props {
    onChange: (range: [IDate, IDate], presetLabel: PeriodLabel | null) => void;
    value?: [IDate | null, IDate | null];
    selectedPreset?: PeriodLabel | null;
    disabled?: boolean;
    className?: string;
    label?: string;
    min?: IDate | null;
    max?: IDate | null;
    selectSize?: BluemateControlSize;
    selectShape?: BluemateControlShape;
    /** Renders the trigger without its box shadow (e.g. inside dialogs). Defaults to false. */
    noShadow?: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 value — so mixed
     * controls in one form read as one family. Defaults to false. */
    uniformFont?: boolean;
    /** Which presets to offer, in order. Defaults to the rolling-window set the
     * filter views use; pass an explicit list to add the closed-window presets
     * (previous week / previous month) or to drop ones a view has no use for. */
    presets?: Record<Partial<PeriodLabel>, PeriodRangeFunc>;
    /** `select` (default) is the labelled pill. `menu` collapses the trigger to
     * a round dots button, for views that put their common periods in a
     * segmented toggle next to it and keep only the rest in here. The button
     * highlights while one of ITS options is the active selection, so the two
     * controls never look selected at the same time. */
    variant?: 'select' | 'menu';
    /** Optional max height (px or rem) of the select items
     * If not given, the list grows to fit all items*/
    itemsMaxHeight?: string | number;
}

function BluematePeriodSelector(props: Props) {
    const {t} = useTranslation();
    const {
        onChange,
        value,
        selectedPreset,
        disabled,
        className,
        label,
        min,
        max,
        selectShape = 'square',
        selectSize = 'normal',
        noShadow = false,
        uniformFont = false,
        presets = PRESET_PERIODS,
        variant = 'select',
        itemsMaxHeight = '25rem'
    } = props;
    const [open, setOpen] = useState(false);
    const [customMode, setCustomMode] = useState(false);
    const [customStart, setCustomStart] = useState<IDate | null>(null);
    const [customEnd, setCustomEnd] = useState<IDate | null>(null);
    const [internalPreset, setInternalPreset] = useState<PeriodLabel | null>(DEFAULT_PRESET);
    const [startPickerKey, setStartPickerKey] = useState(0);
    const [endPickerKey, setEndPickerKey] = useState(0);
    const [itemsWidth, setItemsWidth] = useState<number | null>(null);
    const [moreIconVisible, setMoreIconVisible] = useState(false);
    const rootStyle: CSSProperties | undefined = useMemo(() => {
        if (itemsWidth === null) return undefined;
        return {width: itemsWidth + 'px'};
    }, [itemsWidth]);
    const container = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    const lastItemRef = useRef<HTMLLabelElement>(null);
    const mounted = useRef(false);
    const resizeObserver = useRef(new ResizeObserver(onResizeEvent));

    function onItemsIntersection(entries: IntersectionObserverEntry[]) {
        if (!entries.length) return;
        // Only one item is observed
        setMoreIconVisible(!entries[0].isIntersecting);
    }

    function onScrollItems() {
        // requestAnimationFrame(() => {
        //     if (!itemsRef.current) return;
        //     const icon = document.getElementById('more-icon');
        //     if (!icon) return;
        //     const scrollTop = itemsRef.current.scrollTop;
        //     icon.style.setProperty('transform', `translate(-50%, ${scrollTop}px)`);
        // });
    }

    function onResizeEvent(entries: Array<ResizeObserverEntry>) {
        for (const entry of entries) {
            if (entry.target === itemsRef.current) {
                const nextWidth = entry.target.getBoundingClientRect().width;
                setItemsWidth((prev: number) => (prev === nextWidth ? prev : nextWidth));
            }
        }
    }

    const effectivePreset = selectedPreset !== undefined ? selectedPreset : internalPreset;

    const displayLabel = useMemo(() => {
        if (customMode) return t('controls.periodSelector.customEllipsis', 'Aangepast...');
        if (effectivePreset) return translatePeriodLabel(effectivePreset, t);
        if (!value) return t('controls.periodSelector.selectPeriod', 'Selecteer periode');
        const [start, end] = value;
        if (!start && !end) return t('controls.periodSelector.all', 'Alles');
        if (!start || !end) return t('controls.periodSelector.selectPeriod', 'Selecteer periode');
        return t('controls.periodSelector.custom', 'Aangepast');
    }, [value, effectivePreset, customMode, t]);

    function onClickElement() {
        if (disabled) return;
        if (!open) {
            const customActive = effectivePreset === null;
            // Auto-entering the custom view (a custom range is active) must
            // seed the pickers from the current value — after a remount the
            // local picker state starts empty while the range is still set.
            if (customActive && value) {
                setCustomStart(value[0]);
                setCustomEnd(value[1]);
            }
            setCustomMode(customActive);
        }
        setOpen(!open);
    }

    function onClickPreset(preset: PresetPeriod) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            const [start, end] = preset.getRange();
            if (start && end) {
                onChange([start, end], preset.label);
            } else {
                onChange([IDate.now().subtract(10, 'years').startOf('day'), IDate.now().endOf('day')], preset.label);
            }
            setInternalPreset(preset.label);
            setOpen(false);
        };
    }

    function onClickCustom(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (value) {
            const [start, end] = value;
            setCustomStart(start);
            setCustomEnd(end);
        } else {
            setCustomStart(IDate.now().subtract(1, 'month').startOf('day'));
            setCustomEnd(IDate.now().endOf('day'));
        }
        setCustomMode(true);
    }

    function onClickBack(evt: React.MouseEvent) {
        evt.stopPropagation();
        setCustomMode(false);
    }

    function onChangeCustomStart(value: IDate | null) {
        setCustomStart(value ? value.startOf('day') : null);
    }

    function onChangeCustomEnd(value: IDate | null) {
        setCustomEnd(value ? value.endOf('day') : null);
    }

    function onApplyRange(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (!customStart || !customEnd) return;
        onChange([customStart, customEnd], null);
        setInternalPreset(null);
        setOpen(false);
    }

    function determineItemsWidth() {
        const items = itemsRef.current;
        if (!items) return;
        // Measure the list's natural content width with the `min-width: 100%`
        // stretch neutralized: the rendered width tracks the root, and the
        // root width is derived from this measurement — measuring the rendered
        // width feeds the root width back into itself and grows the control by
        // the 0.75rem offset on every open/close cycle.
        const prevMinWidth = items.style.minWidth;
        items.style.minWidth = '0';
        const nextWidth = items.getBoundingClientRect().width;
        items.style.minWidth = prevMinWidth;
        if (!nextWidth) return;
        setItemsWidth(prev => (prev === nextWidth ? prev : nextWidth));
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className='bluemate-period-selector-label' onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function renderValue() {
        return (
            <label className='bluemate-period-selector-value' onClick={onClickElement}>
                {displayLabel}
            </label>
        );
    }

    // Highlighted while the active selection came from this menu — a custom
    // range (null preset) or one of the presets it offers. Anything else means
    // a sibling control owns the selection.
    function isMenuActive() {
        if (effectivePreset === null) return true;
        return Object.keys(presets).includes(effectivePreset);
    }

    function renderMenuTrigger() {
        return (
            <div
                className={classNames('bluemate-period-selector-menu-button', isMenuActive() && 'active')}
                onClick={onClickElement}
            >
                <AwesomeIcon name='ellipsis-v' />
            </div>
        );
    }

    function renderArrow() {
        return (
            <AwesomeIcon
                name='chevron-down'
                onClick={onClickElement}
                className={classNames('bluemate-period-selector-arrow', open && 'open')}
            />
        );
    }

    function renderPresetItems() {
        const isCustomActive = effectivePreset === null;
        return (
            <>
                {Object.entries(presets).map(([label, getRange]: [PeriodLabel, PeriodRangeFunc], index) => {
                    const selected = effectivePreset === label;
                    return (
                        <label
                            key={index}
                            className={classNames('bluemate-period-selector-item', selected && 'selected')}
                            onClick={onClickPreset({label, getRange})}
                        >
                            <span>{translatePeriodLabel(label, t)}</span>
                            {selected && <AwesomeIcon name='check' className='bluemate-period-selector-check' />}
                        </label>
                    );
                })}
                <label
                    className={classNames('bluemate-period-selector-item custom', isCustomActive && 'selected')}
                    onClick={onClickCustom}
                    ref={lastItemRef}
                >
                    <span>{t('controls.periodSelector.customEllipsis', 'Aangepast...')}</span>
                    {isCustomActive && <AwesomeIcon name='check' className='bluemate-period-selector-check' />}
                </label>
                {moreIconVisible && open && (
                    <div className='more-icon-container' id='more-icon'>
                        <AwesomeIcon name='chevron-down' className='more-icon' />
                    </div>
                )}
            </>
        );
    }

    function resetPickers() {
        setStartPickerKey(k => k + 1);
        setEndPickerKey(k => k + 1);
    }

    function renderCustomRangeView() {
        const canApply = !!customStart && !!customEnd;
        return (
            <div
                className='custom-range-view'
                onClickCapture={(e: React.MouseEvent<HTMLDivElement>) => {
                    if (!(e.target as HTMLElement).closest('.dvrd-date-picker')) {
                        resetPickers();
                    }
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                }}
            >
                <div className='custom-range-header'>
                    <span className='custom-range-title'>
                        {t('controls.periodSelector.customRange', 'Aangepast bereik')}
                    </span>
                    <span className='custom-range-back' onClick={onClickBack}>
                        {t('controls.periodSelector.back', 'Terug')}
                    </span>
                </div>
                <div
                    onClickCapture={() => setEndPickerKey(k => k + 1)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <DvrdDatePicker
                        key={startPickerKey}
                        onChange={onChangeCustomStart}
                        value={customStart}
                        label={t('controls.periodSelector.startDate', 'Startdatum')}
                        placeholder={t('controls.periodSelector.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        min={min}
                        max={customEnd ?? max}
                        allowBodyScroll
                        closeOnChange
                    />
                </div>
                <div
                    onClickCapture={() => setStartPickerKey(k => k + 1)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <DvrdDatePicker
                        key={endPickerKey}
                        onChange={onChangeCustomEnd}
                        value={customEnd}
                        label={t('controls.periodSelector.endDate', 'Einddatum')}
                        placeholder={t('controls.periodSelector.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        min={customStart ?? min}
                        max={max}
                        allowBodyScroll
                        closeOnChange
                    />
                </div>
                <BluemateButton
                    label={t('controls.periodSelector.applyRange', 'Bereik toepassen')}
                    onClick={onApplyRange}
                    color='blue'
                    size='small'
                    align='center'
                    disabled={!canApply}
                    className='custom-range-apply-btn'
                />
            </div>
        );
    }

    function renderItems() {
        const style: CSSProperties | undefined = itemsMaxHeight
            ? {maxHeight: itemsMaxHeight, overflowY: 'auto'}
            : undefined;
        return (
            <div
                className={classNames('bluemate-period-selector-items', open && !disabled && 'open', 'bottom')}
                style={style}
                ref={itemsRef}
                onScroll={onScrollItems}
            >
                {customMode ? renderCustomRangeView() : renderPresetItems()}
            </div>
        );
    }

    function clickListener() {
        if (!hasHover(container.current)) {
            setOpen(false);
        }
    }

    const repositionCalendars = useCallback(() => {
        requestAnimationFrame(() => {
            const el = container.current;
            if (!el) return;
            const datePickers = el.querySelectorAll('.custom-range-view .dvrd-date-picker');
            datePickers.forEach(dp => {
                const bg = dp.querySelector('.with-background') as HTMLElement | null;
                if (!bg) return;
                const pickerEl = bg.querySelector('.bluemate-date-picker-popup') as HTMLElement | null;
                if (!pickerEl) return;
                const pH = pickerEl.offsetHeight;
                const pW = pickerEl.offsetWidth;
                if (!pH || !pW) return;

                const anchor = dp.querySelector('.value-container') as HTMLElement | null;
                if (!anchor) return;
                const rect = anchor.getBoundingClientRect();
                const vh = window.innerHeight;
                const vw = window.innerWidth;
                const gap = 4;
                const buffer = 16; // 1rem

                let top: number;
                let left: number;

                const spaceBelow = vh - rect.bottom;
                const spaceRight = vw - rect.right;
                const spaceLeft = rect.left;

                if (spaceBelow >= pH + gap * 2 + buffer) {
                    // Below input
                    top = rect.bottom + gap;
                    left = rect.left;
                } else if (spaceRight >= pW + gap * 2 + buffer) {
                    // Right of input
                    top = rect.top;
                    left = rect.right + gap;
                } else if (spaceLeft >= pW + gap * 2 + buffer) {
                    // Left of input
                    top = rect.top;
                    left = rect.left - pW - gap;
                } else {
                    // Fallback: side with most horizontal space
                    if (spaceRight >= spaceLeft) {
                        left = Math.max(gap, vw - pW - gap);
                        top = rect.top;
                    } else {
                        left = gap;
                        top = rect.top;
                    }
                }

                // Clamp to viewport
                if (top + pH > vh - gap) top = Math.max(gap, vh - pH - gap);
                if (top < gap) top = gap;
                if (left + pW > vw - gap) left = Math.max(gap, vw - pW - gap);
                if (left < gap) left = gap;

                bg.style.setProperty('top', `${top}px`, 'important');
                bg.style.setProperty('left', `${left}px`, 'important');
            });
        });
    }, []);

    const closePickers = useCallback(() => {
        setStartPickerKey(k => k + 1);
        setEndPickerKey(k => k + 1);
    }, []);

    useEffect(() => {
        if (mounted.current) return;
        mounted.current = true;
        if (value) return;
        const getRange = presets[DEFAULT_PRESET];
        if (!getRange) return;
        const [start, end] = getRange();
        if (start && end) onChange([start, end], DEFAULT_PRESET);
    }, []);

    useEffect(() => {
        if (!open) {
            setCustomMode(false);
        }
        window.setTimeout(() => {
            if (open) document.addEventListener('click', clickListener);
            else document.removeEventListener('click', clickListener);
        });
    }, [open]);

    useEffect(() => {
        if (!customMode || !open) return;
        const el = container.current;
        if (!el) return;

        const observer = new MutationObserver(repositionCalendars);
        observer.observe(el, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style']});

        const page = document.querySelector('div.page');
        if (page) page.addEventListener('scroll', closePickers, true);
        window.addEventListener('resize', repositionCalendars);
        repositionCalendars();

        return () => {
            observer.disconnect();
            const page = document.querySelector('div.page');
            if (page) page.removeEventListener('scroll', closePickers, true);
            window.removeEventListener('resize', repositionCalendars);
        };
    }, [customMode, open, repositionCalendars, closePickers]);

    useEffect(() => {
        determineItemsWidth();
    }, [customMode, open]);

    useEffect(() => {
        const target = itemsRef.current;
        if (target) resizeObserver.current.observe(target);
        return function () {
            if (target) resizeObserver.current.unobserve(target);
        };
    }, [itemsRef.current]);

    useEffect(() => {
        if (!itemsRef.current || !lastItemRef.current) return;
        const observer = new IntersectionObserver(onItemsIntersection, {root: itemsRef.current});
        observer.observe(lastItemRef.current);
        return function () {
            observer.disconnect();
        };
    }, [itemsRef.current, lastItemRef.current]);

    return (
        <div
            className={classNames(
                'bluemate-period-selector',
                `variant-${variant}`,
                disabled && 'disabled',
                open && 'open',
                noShadow && 'no-shadow',
                uniformFont && 'uniform-font',
                className,
                `shape-${selectShape}`,
                `size-${selectSize}`
            )}
            ref={container}
            onClick={onClickElement}
            style={rootStyle}
        >
            {variant === 'menu' ? (
                renderMenuTrigger()
            ) : (
                <>
                    {renderLabel()}
                    <div className='content-container'>
                        {renderValue()}
                        {renderArrow()}
                    </div>
                </>
            )}
            {renderItems()}
        </div>
    );
}

export default BluematePeriodSelector;
